import { useQuery } from "@tanstack/react-query";

import { apiGet, apiPost } from "@/lib/api";

export interface PublishTargetStatus {
  id: string;
  display_name: string;
  enabled: boolean;
  available: boolean;
  supported_modes: string[];
  error_message: string | null;
}

interface PublishTargetList {
  items: PublishTargetStatus[];
}

export interface OutboxRecord {
  id: string;
  inbox_id: string | null;
  destination_type: string;
  destination_id: string | null;
  payload: Record<string, unknown>;
  status: string;
  attempt_count: number;
  last_attempt_at: string | null;
  last_error: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string | null;
}

interface OutboxList {
  total: number;
  items: OutboxRecord[];
}

export interface OutboxRecheckResponse {
  record: OutboxRecord;
  target_available: boolean;
  validation_findings: Array<{
    check: string;
    severity: string;
    message: string;
  }>;
  error_message: string | null;
}

export interface OutboxQueryParams {
  status?: string;
  destination_type?: string;
  limit?: number;
  offset?: number;
}

export function usePublishTargets() {
  return useQuery<PublishTargetList>({
    queryKey: ["publish-targets"],
    queryFn: () => apiGet<PublishTargetList>("/api/v1/targets"),
    refetchInterval: 30_000,
  });
}

export function useOutboxRecords(params: OutboxQueryParams) {
  const queryParams: Record<string, string | number | boolean | undefined> = {
    status: params.status,
    destination_type: params.destination_type,
    limit: params.limit,
    offset: params.offset,
  };
  return useQuery<OutboxList>({
    queryKey: ["outbox", params],
    queryFn: () => apiGet<OutboxList>("/api/v1/outbox", queryParams),
    placeholderData: (prev) => prev,
    refetchInterval: 30_000,
  });
}

export async function recheckOutboxRecord(outboxId: string): Promise<OutboxRecheckResponse> {
  return apiPost<OutboxRecheckResponse>(`/api/v1/outbox/${outboxId}/recheck`);
}
